import { ALERT_TYPES } from 'my-purchases/constants/alertTypes'
import type { DELIVERY_METHODS } from 'my-purchases/constants/deliveryMethods'
import { ORDER_STATUS } from 'my-purchases/constants/orderStatus'
import { ORDER_TYPES } from 'my-purchases/constants/orderTypes'
import type { Order, ResponseOrder } from 'my-purchases/types/purchaseList'
import { dateFormatter } from 'utils/dateFormatter/dateFormatter'

export function getStatus({
	order,
	locale,
	isActive,
}: {
	order: Order
	locale: string
	isActive: boolean
}) {
	const {
		type,
		alert,
		status,
		statusDate,
		deliveryMethod,
		hasSomeReturnedItems,
		hasSomeOutOfStockItems,
		hasSomeReturnRequestedItems,
	} = order
	const hasAlert =
		!!alert &&
		Object.keys(ALERT_TYPES).some(
			(alertType) => alertType.toLocaleLowerCase() === alert.toLocaleLowerCase()
		)

	const title =
		type === ORDER_TYPES.STORE
			? 'myPurchases.inStore.title'
			: `myPurchases.status_${status}.title`

	return {
		title,
		subtitle: getSubtitle({
			type,
			status,
			isActive,
			statusDate,
			deliveryMethod,
			hasSomeReturnedItems,
			hasSomeReturnRequestedItems,
		}),
		placeholders: getPlaceholder({ order, locale }),
		...((hasAlert || hasSomeOutOfStockItems) && {
			alert: getAlert({ status, alert, hasSomeOutOfStockItems }),
		}),
	}
}

const getSubtitle = ({
	type,
	status,
	isActive,
	statusDate,
	deliveryMethod,
	hasSomeReturnedItems,
	hasSomeReturnRequestedItems,
}: {
	type: ORDER_TYPES
	status: ORDER_STATUS
	isActive: boolean
	statusDate: string | null
	deliveryMethod: DELIVERY_METHODS
	hasSomeReturnedItems: boolean
	hasSomeReturnRequestedItems: boolean
}) => {
	if (type === ORDER_TYPES.STORE) {
		return 'myPurchases.inStore.subtitle'
	}

	if (hasSomeReturnedItems) {
		return 'myPurchases.status_delivered.returnedItems.subtitle'
	}

	if (hasSomeReturnRequestedItems) {
		return 'myPurchases.status_delivered.returnRequested.subtitle'
	}

	if (status === ORDER_STATUS.shipped) {
		return `myPurchases.status_${status}.${deliveryMethod}.subtitle`
	}

	if (status === ORDER_STATUS.delivered && !isActive) {
		return 'myPurchases.status_delivered.history.subtitle'
	}

	if (
		!statusDate &&
		[ORDER_STATUS.cancelled, ORDER_STATUS.rejected].includes(status)
	) {
		return 'myPurchases.status_refunded.subtitle'
	}

	return `myPurchases.status_${status}.subtitle`
}

const getAlert = ({
	status,
	alert,
	hasSomeOutOfStockItems,
}: {
	status: ORDER_STATUS
	alert: ALERT_TYPES | null
	hasSomeOutOfStockItems: boolean
}) => {
	if (status === ORDER_STATUS.in_preparation && hasSomeOutOfStockItems) {
		return 'myPurchases.alert_outOfStockItems.subtitle'
	}

	if (alert) {
		return `myPurchases.alert_${alert}.subtitle`
	}

	return ''
}

const getPlaceholder = ({
	order,
	locale,
}: {
	order: ResponseOrder
	locale: string
}) => {
	const { status, statusDate } = order

	if (statusDate) {
		return {
			1: formatDate(statusDate, locale),
		}
	}

	if (status === ORDER_STATUS.shipped) {
		const { delivery } = order ?? {}
		const { fullAddress } = delivery ?? {}

		return {
			1: fullAddress,
		}
	}

	return null
}

const formatDate = (dateString: string, locale: string) =>
	dateFormatter({
		dateString,
		locale,
		options: {
			day: '2-digit',
			month: 'long',
			weekday: 'long',
		},
	})
